import { useList, useOne } from '@refinedev/core';
import { Link } from 'react-router';
import { useSession } from '../../../lib/session.js';
import { Bento, BentoCell, BentoStat, HeroFigure } from '../../../components/Bento.js';
import { BalanceTable } from '../../../components/BalanceTable.js';
import { DocumentList } from '../../../components/DocumentList.js';

/**
 * The resident's home screen, as a bento.
 *
 * The layout encodes the ranking. A resident opens this page to answer one
 * question — what do I owe — so that is the hero figure and the widest cell, and
 * everything else is arranged around it. Address, unit and contact details are
 * reference material they already know; they get small cells.
 *
 * ---------------------------------------------------------------------------
 * The hero is only rendered when there is exactly one balance
 * ---------------------------------------------------------------------------
 *
 * With two leases there are two balances and no single number to lead with, and
 * the one thing this file must never do is add them up. Money is a decimal string
 * from the server (invariant 2); `Number(a) + Number(b)` back to a string is how a
 * portal starts disagreeing with the ledger by a cent, and a resident who pays the
 * number on this page then still owes money is a support call that ends in a late
 * fee. So the multi-lease case falls back to the per-lease table, which is the
 * honest shape of the answer. A summed total, if it is ever wanted, is a SUM over a
 * Decimal column in SQL — not a reduce in a component.
 *
 * ---------------------------------------------------------------------------
 * What a moved-out resident sees
 * ---------------------------------------------------------------------------
 *
 * `app_tenant_can_see_unit()` joins through `lease_tenants` with no filter on
 * `removed_on`, so someone who moved out last month still sees their unit and their
 * documents. That is deliberate and not a leak: the 30-day damages clock is still
 * running, they are still liable for the balance, and a portal that goes blank on
 * move-out day is a portal that hides the notice they most need to read.
 */
interface TenantRecord {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  leaseCount: number;
  achAccess: { revokedAt: string | null };
}

interface UnitRow {
  id: string;
  propertyId: string;
  propertyName: string;
  label: string;
  bedrooms: number;
  bathrooms: string;
  status: string;
}

interface PropertyRow {
  id: string;
  name: string;
  address: { line1: string; line2: string | null; city: string; state: string; postalCode: string };
}

export function TenantHome() {
  const { state } = useSession();
  const tenantId = state.status === 'signed-in' ? state.me.actor.tenantId : null;

  const { data: tenantData } = useOne<TenantRecord>({
    resource: 'tenants',
    id: tenantId ?? '',
    queryOptions: { enabled: Boolean(tenantId) },
  });

  // Unfiltered on purpose. `units_tenant_select` scopes this to the unit(s) on this
  // resident's leases — asking for "my unit" and asking for "all units" are the same
  // request under RLS, and the second one cannot be got wrong.
  const { data: unitsData, isLoading: unitsLoading } = useList<UnitRow>({
    resource: 'units',
    pagination: { mode: 'off' },
  });

  const { data: propertiesData } = useList<PropertyRow>({
    resource: 'properties',
    pagination: { mode: 'off' },
  });

  if (state.status !== 'signed-in') return null;
  const { me } = state;

  const tenant = tenantData?.data;
  const units = unitsData?.data ?? [];
  const unit = units[0];
  const property = propertiesData?.data.find((row) => row.id === unit?.propertyId);

  const single = me.balances.length === 1 ? me.balances[0] : null;
  // A PENDING/PROCESSING ACH payment doesn't touch the charge's balance until
  // it settles (invariant 4 — FIFO auto-allocation runs on settlement, not on
  // submission), so a resident who just paid still shows a full balance for
  // the few business days the debit takes to clear. Boolean presence check
  // only — never arithmetic on the two decimal strings (invariant 2); see
  // hasPendingPayment below for why the hero shows both figures verbatim
  // instead of a computed "remaining" amount.
  const hasPendingPayment = (balance: { pendingPaymentAmount?: string | undefined }) =>
    Number(balance.pendingPaymentAmount ?? '0') > 0;
  // "Something to worry about" is late AND not already covered by a payment
  // in flight — the office already has the money coming, so the attention
  // tone (and the "contact the office" caption below) would be misleading.
  const overdue = me.balances.some(
    (balance) => balance.maxDaysLate > 0 && !hasPendingPayment(balance),
  );

  return (
    <section>
      <h1>Welcome{tenant ? `, ${tenant.firstName}` : ''}</h1>
      <p className="muted">Welcome to your customized dashboard.</p>

      <Bento>
        {/* ---- The one question this page answers ---------------------------- */}
        <BentoCell span="wide" tone={overdue ? 'attention' : 'default'}>
          {single && hasPendingPayment(single) ? (
            <HeroFigure
              label="Payment pending"
              tone="pending"
              // Both figures shown verbatim, never subtracted — the "amount
              // still needed" isn't computed here, per invariant 2. Once the
              // payment settles, this lease drops out of `me.balances`
              // entirely (the balance view only returns rows > 0) and the
              // tenant sees the $0.00 branch below on their next visit.
              value={`$${single.totalBalance}`}
              caption={
                <>
                  A <strong>${single.pendingPaymentAmount}</strong> payment is processing and
                  typically settles within a few business days.{' '}
                  {single.maxDaysLate > 0
                    ? 'This balance will update once it clears — no need to pay again.'
                    : "You're all set — no action needed while it clears."}
                </>
              }
            />
          ) : single ? (
            <HeroFigure
              label={overdue ? 'Past due' : 'Balance'}
              // No payment in flight, so this IS the full amount currently
              // owed — red regardless of the overdue/not-yet-late split
              // above, which only governs the "attention" tone on the cell
              // itself. "Payment pending" (orange) above is the one other
              // state a nonzero balance can be in.
              tone="due"
              // The decimal string, rendered verbatim. Never parsed.
              value={`$${single.totalBalance}`}
              caption={
                single.maxDaysLate > 0 ? (
                  <>
                    <strong>{single.maxDaysLate} days late.</strong> ${single.pastDueBalance} of
                    this is past due. Please contact the office with questions or concerns.
                  </>
                ) : (
                  'Nothing is currently overdue.'
                )
              }
            />
          ) : me.balances.length === 0 ? (
            <HeroFigure
              label="Balance"
              value="$0.00"
              caption="Your account is up to date. Nothing is currently owed."
            />
          ) : (
            <>
              <h2 className="bento__title">Balances</h2>
              <p className="bento__hint">
                You are named on more than one lease. Each carries its own balance and is paid
                separately — they are shown apart rather than added together, because there is no
                single account to pay against.
              </p>
              <BalanceTable balances={me.balances} showPendingPayment />
            </>
          )}
        </BentoCell>

        {/* ---- Paying, which is the obvious next question --------------------
             A one-column cell is roughly 13rem wide at the shell's max width, which
             is about six words per line. Anything longer than a sentence here
             becomes a tall narrow column that stretches the whole hero row to match
             it — so the short version lives in the tile and the explanation lives on
             its own page (/portal/tenant/pay), which is also where the real Stripe
             form goes once that's wired. */}
        <BentoCell
          span="sm"
          title="Pay Today"
          tone={tenant?.achAccess.revokedAt ? 'attention' : 'default'}
        >
          <p className="bento__hint">
            {tenant?.achAccess.revokedAt
              ? 'ACH is not available on your account right now.'
              : 'Pay as you have paid in the past.'}
          </p>
          <p className="bento__sub">
            <Link
              to="/portal/tenant/pay"
              className={`pill pill--action${tenant?.achAccess.revokedAt ? '' : ' pill--positive'}`}
            >
              {tenant?.achAccess.revokedAt ? 'See how to pay →' : 'Payment options →'}
            </Link>
          </p>
        </BentoCell>

        {/* ---- Where they live ---------------------------------------------- */}
        <BentoCell span="md" title="Your home">
          {unitsLoading ? (
            <p className="bento__hint">Loading…</p>
          ) : !unit ? (
            <p className="bento__hint">
              No unit is linked to your account yet. If you have just signed a lease, the office
              links it when the lease is activated.
            </p>
          ) : (
            <>
              <BentoStat label={`Unit ${unit.label}`} value={unit.propertyName} />
              <dl className="deflist">
                <div className="deflist__row">
                  <dt className="deflist__key">Bedrooms</dt>
                  <dd className="deflist__value">{unit.bedrooms}</dd>
                </div>
                <div className="deflist__row">
                  <dt className="deflist__key">Bathrooms</dt>
                  <dd className="deflist__value">{unit.bathrooms}</dd>
                </div>
                <br />
              </dl>
              {property ? (
                <address className="bento__sub">
                  {property.address.line1}
                  {property.address.line2 ? <>, {property.address.line2}</> : null}
                  <br />
                  {property.address.city}, {property.address.state} {property.address.postalCode}
                </address>
              ) : null}
              {units.length > 1 ? (
                <p className="bento__sub">
                  {units.length - 1} other unit(s) are linked to your account, including any you
                  have moved out of.
                </p>
              ) : null}
            </>
          )}
        </BentoCell>

        {/* ---- Who we think they are ----------------------------------------- */}
        <BentoCell span="md" title="Your details">
          {tenant ? (
            <>
              <BentoStat label="On file" value={`${tenant.firstName} ${tenant.lastName}`} />
              <dl className="deflist">
                <div className="deflist__row">
                  <dt className="deflist__key">Email</dt>
                  <dd className="deflist__value">{tenant.email}</dd>
                </div>
                <div className="deflist__row">
                  <dt className="deflist__key">Phone</dt>
                  <dd className="deflist__value">{tenant.phone ?? 'None on file'}</dd>
                </div>
              </dl>
              <p className="bento__sub">
                Contact the office to change your details on file. <br />
                Check with our staff to make sure your forwarding address is valid if you move.
              </p>
            </>
          ) : (
            <p className="bento__hint">Loading your details…</p>
          )}
        </BentoCell>

        {/* ---- Paperwork ------------------------------------------------------ */}
        <BentoCell span="full" title="Your documents">
          <p className="bento__hint">
            Your lease, notices and checklists — anything the office has shared with you.
          </p>
          <DocumentList emptyMessage="Nothing has been shared with you yet." />
        </BentoCell>
      </Bento>
    </section>
  );
}
